import {
  type PropsWithChildren,
  useContext,
  useMemo,
  createContext,
} from 'react';

import type { DataTableConfig } from '../DataTable/public.api.js';
import { deserializeDataTableConfig } from '../utils/datatable-config-utils.js';

/**
 * @internal
 */
const DataTableConfigContext = createContext<DataTableConfig>({});

/**
 * Provider for the DataTable API config
 * @public
 */
export function DataTableConfigProvider(
  props: PropsWithChildren<{ value: DataTableConfig | string }>,
) {
  const { children, value } = props;

  const deserializedConfig = useMemo(() => {
    return typeof value === 'string'
      ? deserializeDataTableConfig(value)
      : value;
  }, [value]);

  return (
    <DataTableConfigContext.Provider value={deserializedConfig}>
      {children}
    </DataTableConfigContext.Provider>
  );
}

/**
 * Internal helper function that returns the value of
 * `DataTable`'s config options based on the `key`.
 * @internal
 */
export function useDataTableConfigElement<T extends keyof DataTableConfig>(
  key: T,
): DataTableConfig[T] | undefined {
  const contextConfig = useContext(DataTableConfigContext);

  if (
    typeof contextConfig?.[key] === 'undefined' ||
    contextConfig?.[key] === null
  ) {
    return undefined;
  }

  return contextConfig[key];
}
